import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Tag, Package, ArrowRight, MapPin, MessageCircle, Cog, Gauge, LayoutGrid, Circle, Zap, ArrowDownUp, Disc } from 'lucide-react';
import { getProducts, getCategories, getFavoriteIds } from '../lib/api';
import { useAuthStore } from '../lib/authStore';
import { STORE_MAPS_URL, STORE_PHONE } from '../lib/constants';
import BrandName from '../components/BrandName';
import ProductCard from '../components/product/ProductCard';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [promos, setPromos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, promoRes, catRes] = await Promise.all([
          getProducts({ destaque: 'true', limit: 8 }),
          getProducts({ promocao: 'true', limit: 8 }),
          getCategories(),
        ]);
        setFeatured(featRes.data.products || []);
        setPromos(promoRes.data.products || []);
        setCategories(catRes.data?.slice(0, 6) || []);
        if (isAuthenticated) {
          try { const favRes = await getFavoriteIds(); setFavoriteIds(favRes.data || []); } catch(e) {}
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [isAuthenticated]);

  const toggleFav = (id) => {
    setFavoriteIds(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      <Header />
      <main className="flex-1 pb-20 md:pb-0">
        {/* Hero */}
        <section data-testid="hero-section" className="relative overflow-hidden" style={{ minHeight: '420px' }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1705217670327-769522ad3ce3?w=1200&q=80)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#09090B]" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at center, rgba(220,38,38,0.15) 0%, transparent 70%)' }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
            <div className="text-center">
              <h1 translate="no" className="text-4xl md:text-7xl mb-3" data-testid="hero-title">
                <span className="text-white font-bold uppercase tracking-tighter block" style={{ fontFamily: 'Teko, sans-serif' }}>Bem-vindo à </span>
                <span className="font-bold uppercase tracking-tighter" style={{ fontFamily: 'Teko, sans-serif' }}>
                  <span className="text-white">MOTO</span><span className="text-red-500">VELOZ</span>
                </span>
              </h1>
              <p translate="no" className="text-lg md:text-2xl text-white font-bold uppercase tracking-widest mb-2">Sua Jornada. Nossa Peça</p>
              <p className="text-zinc-400 mb-8 max-w-xl mx-auto">As melhores peças e acessórios para sua motocicleta</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/produtos" data-testid="hero-catalog-btn" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 font-bold uppercase tracking-wider transition-all -skew-x-3 hover:skew-x-0">
                  <Package className="h-5 w-5" /> Ver Produtos
                </Link>
                <Link to="/produtos?promocao=true" data-testid="hero-promo-btn" className="inline-flex items-center justify-center gap-2 border-2 border-red-600 text-red-500 hover:bg-red-600 hover:text-white px-8 py-3 font-bold uppercase tracking-wider transition-all -skew-x-3 hover:skew-x-0">
                  <Tag className="h-5 w-5" /> Ver Promoções
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        {categories.length > 0 && (
          <section data-testid="categories-section" className="py-12 bg-zinc-900/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8 text-center" style={{ fontFamily: 'Teko, sans-serif' }}>Categorias</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {categories.map((cat) => {
                  const iconMap = {
                    'MOTOR': Cog,
                    'PAINEL': Gauge,
                    'PADRAO': LayoutGrid,
                    'RODAS': Circle,
                    'ELETRICA': Zap,
                    'SUSPENSÃO': ArrowDownUp,
                    'FREIO': Disc,
                  };
                  const Icon = iconMap[cat.name] || Package;
                  return (
                  <Link key={cat.name} to={`/produtos?categoria=${encodeURIComponent(cat.name)}`}
                    className="bg-zinc-800 hover:bg-red-600 transition-all p-5 text-center group border border-zinc-700 hover:border-red-500" data-testid={`category-${cat.name}`}>
                    <Icon className="h-8 w-8 text-red-500 group-hover:text-white mx-auto mb-2" />
                    <p translate="no" className="text-white font-bold text-xs uppercase tracking-wider">{cat.name}</p>
                    <p className="text-zinc-500 text-[10px] mt-1">{cat.count} itens</p>
                  </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Featured */}
        {featured.length > 0 && (
          <section data-testid="featured-section" className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Star className="h-7 w-7 text-yellow-500 fill-yellow-500" />
                  <h2 className="text-2xl md:text-4xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }}>Destaques</h2>
                </div>
                <Link to="/produtos?destaque=true" className="text-red-500 hover:text-red-400 font-bold text-sm flex items-center gap-1 uppercase tracking-wider">
                  Ver todos <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {featured.map((p) => <ProductCard key={p.id} product={p} isFavorite={favoriteIds.includes(p.id)} onFavoriteToggle={toggleFav} />)}
              </div>
            </div>
          </section>
        )}

        {/* Promos */}
        {promos.length > 0 && (
          <section data-testid="promos-section" className="py-16 bg-zinc-900/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Tag className="h-7 w-7 text-red-500" />
                  <h2 className="text-2xl md:text-4xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }}>Promoções</h2>
                </div>
                <Link to="/produtos?promocao=true" className="text-red-500 hover:text-red-400 font-bold text-sm flex items-center gap-1 uppercase tracking-wider">
                  Ver todas <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {promos.map((p) => <ProductCard key={p.id} product={p} isFavorite={favoriteIds.includes(p.id)} onFavoriteToggle={toggleFav} />)}
              </div>
            </div>
          </section>
        )}

        {/* Location CTA */}
        <section data-testid="location-section" className="py-16 bg-gradient-to-r from-red-900/80 to-red-700/80">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Teko, sans-serif' }}>Visite Nossa Loja</h2>
            <p className="text-red-100 mb-6">Venha conhecer nossos produtos pessoalmente ou entre em contato pelo WhatsApp</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={STORE_MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="maps-route-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-red-700 px-8 py-3 font-bold uppercase tracking-wider transition-all hover:bg-red-50">
                <MapPin className="h-5 w-5" /> Como Chegar
              </a>
              <a href={`https://wa.me/${STORE_PHONE}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white px-8 py-3 font-bold uppercase tracking-wider transition-all">
                <MessageCircle className="h-5 w-5" /> Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
